import {ConsentHistory} from '@/types'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpiredIcon from '@mui/icons-material/Schedule'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Card,
	CardContent,
	Chip,
	Divider,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography
} from '@mui/material'
import * as React from 'react'
import {useEffect, useState} from 'react'
import {Loading, useDataProvider} from 'react-admin'

interface UserConsentHistoryProps {
	userId: number
}

export default function UserConsentHistory({userId}: UserConsentHistoryProps) {
	const [history, setHistory] = useState<ConsentHistory[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const dataProvider = useDataProvider()

	useEffect(() => {
		const fetchHistory = async () => {
			try {
				setLoading(true)
				const result = await dataProvider.getList(`users/${userId}/consents/history`, {
					pagination: {page: 1, perPage: 100},
					sort: {field: 'actionAt', order: 'DESC'},
					filter: {}
				})
				setHistory(result.data)
				setError(null)
			} catch (err) {
				setError('Erreur lors du chargement de l\'historique')
				console.error('Error fetching consent history:', err)
			} finally {
				setLoading(false)
			}
		}

		fetchHistory()
	}, [userId])

	const getActionIcon = (action: string) => {
		switch (action) {
			case 'granted':
			case 'GRANTED':
				return <CheckCircleIcon color="success"/>
			case 'revoked':
			case 'REVOKED':
				return <CancelIcon color="error"/>
			case 'expired':
			case 'EXPIRED':
				return <ExpiredIcon color="warning"/>
			default:
				return <CheckCircleIcon/>
		}
	}

	const getActionColor = (action: string): 'success' | 'error' | 'warning' => {
		switch (action) {
			case 'granted':
			case 'GRANTED':
				return 'success'
			case 'revoked':
			case 'REVOKED':
				return 'error'
			case 'expired':
			case 'EXPIRED':
				return 'warning'
			default:
				return 'success'
		}
	}

	const getActionLabel = (action: string) => {
		switch (action) {
			case 'GRANTED':
			case 'granted':
				return 'Consentement accordé'
			case 'revoked':
			case 'REVOKED':
				return 'Consentement révoqué'
			case 'EXPIRED':
			case 'expired':
				return 'Consentement expiré'
			default:
				return action
		}
	}

	if (loading) return <Loading/>

	if (error) {
		return (
			<Alert severity="error">
				{error}
			</Alert>
		)
	}

	if (history.length === 0) {
		return (
			<Alert severity="info">
				Aucun historique de consentement trouvé pour cet utilisateur.
			</Alert>
		)
	}

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Historique des consentements
			</Typography>

			<Card>
				<CardContent>
					<List>
						{history.map((item, index) => (
							<React.Fragment key={item.id}>
								<ListItem alignItems="flex-start">
									<ListItemIcon>
										{getActionIcon(item.action)}
									</ListItemIcon>
									<ListItemText
										primary={
											<Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
												<Typography variant="h6" component="span">
													{item.consentPurpose.title}
												</Typography>
												<Chip
													label={getActionLabel(item.action)}
													color={getActionColor(item.action)}
													size="small"
												/>
											</Box>
										}
										secondary={
											<Box>
												<Typography variant="body1" color="textSecondary" sx={{mb: 1}}>
													{item.consentPurpose.code} - Version {item.consentPurpose.version}
												</Typography>

												{/*<Typography variant="body2" sx={{mb: 1}}>*/}
												{/*	Source: {item.userAgent}*/}
												{/*</Typography>*/}

												<Typography variant="body1" color="textSecondary">
													Modifié le {item.createdAt ? new Date(item.createdAt).toLocaleDateString('fr-FR', {
													day: '2-digit',
													month: '2-digit',
													year: 'numeric',
													hour: '2-digit',
													minute: '2-digit'
												}) : 'Non disponible'}
												</Typography>

												{/* Détails supplémentaires */}
												<Accordion
													sx={{boxShadow: 'none', '&:before': {display: 'none'}, mt: 1}}>
													<AccordionSummary
														expandIcon={<ExpandMoreIcon/>}
														sx={{
															minHeight: 'auto',
															'& .MuiAccordionSummary-content': {margin: '4px 0'}
														}}
													>
														<Typography variant="caption">
															Voir les détails techniques
														</Typography>
													</AccordionSummary>
													<AccordionDetails sx={{pt: 0}}>
														<Box>
															{item.userAgent && (
																<Typography variant="caption" display="block"
																			sx={{mb: 1}}>
																	<strong>User Agent:</strong> {item.userAgent}
																</Typography>
															)}

															{item.contextData && Object.keys(item.contextData).length > 0 && (
																<Box sx={{mt: 1}}>
																	<Typography variant="caption" display="block"
																				sx={{mb: 1}}>
																		<strong>Métadonnées:</strong>
																	</Typography>
																	<Box component="pre" sx={{
																		fontSize: '0.7rem',
																		backgroundColor: 'action.hover',
																		p: 1,
																		borderRadius: 1,
																		overflow: 'auto',
																		maxWidth: '100%'
																	}}>
																		{JSON.stringify(item.contextData, null, 2)}
																	</Box>
																</Box>
															)}

															<Typography variant="caption" display="block"
																		color="textSecondary">
																Enregistré
																le: {item.createdAt ? new Date(item.createdAt).toLocaleString() : 'Non disponible'}
															</Typography>
														</Box>
													</AccordionDetails>
												</Accordion>
											</Box>
										}
									/>
								</ListItem>
								{index < history.length - 1 && <Divider/>}
							</React.Fragment>
						))}
					</List>
				</CardContent>
			</Card>
		</Box>
	)
}
